import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";

const emptyRow = { name: "", phone: "", email: "", pan: "" };
const MAX_COOWNERS = 6;

/**
 * Lightweight repeating editor for plot co-owners.
 * Each row collects Name (required), Mobile, Email, PAN (optional).
 * Rows with empty names are dropped on the backend by `sanitize_coowners`.
 */
export default function CoOwnersEditor({ value = [], onChange, testIdPrefix = "coowner" }) {
  const rows = value && value.length ? value : [];

  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const add = () => {
    if (rows.length >= MAX_COOWNERS) return;
    onChange([...rows, { ...emptyRow }]);
  };
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="col-span-2 space-y-2">
      <div className="flex items-center justify-between">
        <Label>Co-owners <span className="text-xs text-muted-foreground font-normal">(optional, up to {MAX_COOWNERS})</span></Label>
        <Button type="button" size="sm" variant="outline"
          data-testid={`${testIdPrefix}-add`}
          onClick={add} disabled={rows.length >= MAX_COOWNERS}>
          <Plus className="h-3.5 w-3.5 mr-1"/>Add co-owner
        </Button>
      </div>

      {rows.length === 0 && (
        <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
          No co-owners added. Click "Add co-owner" for jointly-held plots.
        </div>
      )}

      {rows.map((row, i) => (
        <div key={i} data-testid={`${testIdPrefix}-row-${i}`}
          className="grid grid-cols-[1fr_1fr_1.4fr_0.8fr_auto] gap-2 items-end rounded-md border border-border bg-muted/30 p-2">
          <div>
            {i === 0 && <Label className="text-xs">Name *</Label>}
            <Input data-testid={`${testIdPrefix}-name-${i}`}
              value={row.name || ""} placeholder="Full name"
              onChange={(e) => update(i, { name: e.target.value })}/>
          </div>
          <div>
            {i === 0 && <Label className="text-xs">Mobile</Label>}
            <Input data-testid={`${testIdPrefix}-phone-${i}`}
              value={row.phone || ""} placeholder="10-digit mobile"
              onChange={(e) => update(i, { phone: e.target.value })}/>
          </div>
          <div>
            {i === 0 && <Label className="text-xs">Email</Label>}
            <Input data-testid={`${testIdPrefix}-email-${i}`}
              value={row.email || ""} placeholder="name@example.com"
              onChange={(e) => update(i, { email: e.target.value })}/>
          </div>
          <div>
            {i === 0 && <Label className="text-xs">PAN</Label>}
            <Input data-testid={`${testIdPrefix}-pan-${i}`}
              value={row.pan || ""} placeholder="ABCDE1234F" maxLength={10}
              onChange={(e) => update(i, { pan: e.target.value.toUpperCase() })}/>
          </div>
          <Button type="button" size="icon" variant="ghost"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid={`${testIdPrefix}-remove-${i}`}
            onClick={() => remove(i)} title="Remove co-owner">
            <X className="h-4 w-4"/>
          </Button>
        </div>
      ))}
    </div>
  );
}
